import { AnimatePresence } from 'framer-motion';
import { FC, useCallback, useState } from 'react';
import { Crop } from 'react-image-crop';
import styled, { css } from 'styled-components';

import Button from '@/components/Button';
import ShowHideButton from '@/components/ShowHideButton';
import Text from '@/components/Text';
import { checkIfWindows } from '@/utils/helpers';
import { scrollBarStyles } from '@/utils/theme';

import AttachedCountBadge from '../AttachedCountBadge';
import MoreImagesModal from '../MoreImagesModal';
import UploadMultipleImages from '../UploadMultipleImages';
import Filler from '../shared/Filler';
import GeneratedImages from './GeneratedImages';
import NewUploadedImageRow from './NewUploadedImageRow';
import { getWatermarkPosition, getWatermarkTag } from './SettingsModal';
import VariationsStyles from './VariationsStyles';
import VariationsStylesWindows from './VariationsStylesWindows';

export interface ImageData {
  image: File;
  croppedImage?: string;
  previousCrop?: Crop;
  text: string;
  textPosition: string;
  quotes: boolean;
  brakes: boolean;
  divider: string;
  actualPhoto: string;
  /** Mutually exclusive watermark tag: 'none' | 'actual-photo' | 'ai-generated' | 'ai-assisted' */
  watermarkTag?: string;
  /** Where the watermark tag sits: 'bottom' | 'top' */
  watermarkPosition?: string;
  hasMargins: boolean;
  fontSize?: number;
  authorName?: string;
  nameColor?: string;
  authorImage?: string;
  authorImageOriginal?: ImageData[];
  channelImageOption?: string;
  imagePosition?: string;
  imagePositionLeft?: number;
  imagePositionTop?: number;
}

export interface ImageVariation {
  leftImageSrc: string;
  rightImageSrc: string;
  leftWatermarkTag: string;
  leftWatermarkPosition: string;
  rightWatermarkTag: string;
  rightWatermarkPosition: string;
  leftText: string;
  rightText: string;
  leftTextPositon: string;
  leftQuotes: boolean;
  leftBreakes: boolean;
  rightTextPositon: string;
  rightQuotes: boolean;
  rightBreakes: boolean;
  divider: string;
}

export const Box = styled.div`
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  margin-bottom: 24px;
`;

export const HeaderButtons = styled.div`
  display: flex;
  gap: 16px;
`;

export const ButtonHolder = styled.div`
  margin-bottom: 48px;
  display: flex;
  justify-content: flex-end;
  gap: 16px;
`;

export const UploadedImages = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 420px;
  overflow-y: auto;
  ${scrollBarStyles};
  padding-bottom: 8px;
`;

export const AddMoreButton = styled(Button)`
  padding: 0 24px;
`;

export const MoreImages = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-top: 2px solid ${({ theme }) => theme.colors.grayscaleLight};
  margin-left: -24px;
  margin-right: -24px;
`;

export const ImagesBox = styled.div<{
  $hasImages?: boolean;
  $hasLeftBorder?: boolean;
  $reversed?: boolean;
}>`
  padding: 24px;
  ${({ $hasImages }) =>
    $hasImages &&
    css`
      padding-bottom: 0;
      border-bottom: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
    `}
  ${({ $hasLeftBorder }) =>
    $hasLeftBorder &&
    css`
      border-left: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
    `}
`;

export const Header = styled.div`
  padding: 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
`;

export const Heading = styled(Text)`
  margin-bottom: 8px;
`;

export const ResetButton = styled(Button)`
  height: 38px;
  font-size: 14px;
  padding: 0 32px;
`;

export const LabelText = styled(Text)`
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

interface Props {
  inArticle?: boolean;
}

const VariationOne: FC<Props> = ({ inArticle }) => {
  const [showContent, setShowContent] = useState(true);
  const [leftImages, setLeftImages] = useState<ImageData[]>([]);
  const [rightImages, setRightImages] = useState<ImageData[]>([]);
  const [variations, setVariations] = useState<ImageVariation[]>([]);
  const [images, setImages] = useState<string[]>();
  const [showLeftImagesModal, setShowLeftImagesModal] = useState(false);
  const [showRightImagesModal, setShowRightImagesModal] = useState(false);

  const renderVariationStyles = () => {
    if (checkIfWindows()) {
      return <VariationsStylesWindows />;
    }
    return <VariationsStyles />;
  };

  const generateVariations = useCallback(() => {
    if (!leftImages.length || !rightImages.length) return;

    const newVariations = leftImages.flatMap((leftImage) =>
      rightImages.map((rightImage) => ({
        leftImageSrc:
          leftImage.croppedImage ?? URL.createObjectURL(leftImage.image),
        rightImageSrc:
          rightImage.croppedImage ?? URL.createObjectURL(rightImage.image),
        leftWatermarkTag: getWatermarkTag(leftImage),
        leftWatermarkPosition: getWatermarkPosition(leftImage),
        rightWatermarkTag: getWatermarkTag(rightImage),
        rightWatermarkPosition: getWatermarkPosition(rightImage),
        leftText: leftImage.text,
        rightText: rightImage.text,
        leftTextPositon: leftImage.textPosition,
        leftQuotes: leftImage.quotes,
        leftBreakes: leftImage.brakes,
        rightTextPositon: rightImage.textPosition,
        rightQuotes: rightImage.quotes,
        rightBreakes: rightImage.brakes,
        divider: leftImage.divider,
      })),
    );

    setVariations(newVariations);
  }, [leftImages, rightImages]);

  const renderLeftImages = leftImages.map((image, index) => (
        <NewUploadedImageRow
          preview
          settings
          key={`${index}-${image.image.name}-${image.image.lastModified}`}
          imageIndex={index}
          image={image}
          setImages={setLeftImages}
          type="left"
        />
      ));

  const renderRightImages = rightImages.map((image, index) => (
        <NewUploadedImageRow
          preview
          settings
          key={`${index}-${image.image.name}-${image.image.lastModified}`}
          imageIndex={index}
          image={image}
          setImages={setRightImages}
          type="right"
        />
      ));

  const onReset = () => {
    setRightImages([]);
    setLeftImages([]);
    setVariations([]);
    setImages([]);
  };

  return (
    <>
      {renderVariationStyles()}
      <Box>
        <Header>
          <div>
            <Heading $type="button">Left / Right Image + Text</Heading>
            <Text $type="smallHeadline" $color="gray">
              Attach 2 images for left and right side, add text and set needed
              parameters to get results
            </Text>
          </div>
          <HeaderButtons>
            <ResetButton
              color="red"
              onClick={onReset}
              disabled={!leftImages?.length && !rightImages?.length}
            >
              Reset
            </ResetButton>
            <ShowHideButton
              showContent={showContent}
              onClick={() => setShowContent((prev) => !prev)}
            />
          </HeaderButtons>
        </Header>
        {showContent && (
          <div>
            <ImagesBox $hasImages={!!leftImages?.length}>
              <LabelText $type="smallHeadline">Left Images</LabelText>
              {!!leftImages?.length ? (
                <>
                  <UploadedImages>{renderLeftImages}</UploadedImages>
                  <MoreImages>
                    <AttachedCountBadge count={leftImages.length} />
                    <AddMoreButton
                      onClick={() => setShowLeftImagesModal(true)}
                      color="colorized"
                    >
                      Add more images
                    </AddMoreButton>
                  </MoreImages>
                </>
              ) : (
                <UploadMultipleImages
                  coverIndex={1}
                  setImages={setLeftImages}
                />
              )}
            </ImagesBox>
            <ImagesBox $hasImages={!!rightImages?.length}>
              <LabelText $type="smallHeadline">Right Images</LabelText>
              {!!rightImages?.length ? (
                <>
                  <UploadedImages>{renderRightImages}</UploadedImages>
                  <MoreImages>
                    <AttachedCountBadge count={rightImages.length} />
                    <AddMoreButton
                      onClick={() => setShowRightImagesModal(true)}
                      color="colorized"
                    >
                      Add more images
                    </AddMoreButton>
                  </MoreImages>
                </>
              ) : (
                <UploadMultipleImages
                  coverIndex={2}
                  setImages={setRightImages}
                />
              )}
            </ImagesBox>
          </div>
        )}
      </Box>
      <Filler />
      {showContent && (
        <ButtonHolder>
          <Button
            disabled={!leftImages?.length || !rightImages?.length}
            onClick={generateVariations}
          >
            {!!variations?.length ? 'Regenerate' : 'Generate'}
          </Button>
        </ButtonHolder>
      )}
      <GeneratedImages
        variations={variations}
        images={images}
        setImages={setImages}
        inArticle={inArticle}
        onReset={onReset}
      />
      <AnimatePresence>
        {!!showLeftImagesModal && (
          <MoreImagesModal
            onClose={() => setShowLeftImagesModal(false)}
            setImages={setLeftImages}
            coverIndex={1}
          />
        )}
        {!!showRightImagesModal && (
          <MoreImagesModal
            onClose={() => setShowRightImagesModal(false)}
            setImages={setRightImages}
            coverIndex={2}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default VariationOne;
